import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Login as ClockInIcon,
  Logout as ClockOutIcon,
  Work,
  LocationOn,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

const TodaysJobsWidget = ({ onJobClick }) => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingJobId, setClockingJobId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchTodaysJobs = useCallback(async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const response = await makeApiCall(
        `api/jobs?date_from=${today}&date_to=${today}&limit=50`
      );
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to fetch today\'s jobs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTodaysJobs();
  }, [fetchTodaysJobs]);

  const handleClockAction = useCallback(async (job, action) => {
    setClockingJobId(job.id);
    try {
      if (!navigator.geolocation) {
        setError(t('common.error'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const endpoint = action === 'in'
            ? `api/jobs/${job.id}/clock-in`
            : `api/jobs/${job.id}/clock-out`;

          const body = action === 'in'
            ? { latitude: position.coords.latitude, longitude: position.coords.longitude, selected_services: [] }
            : { latitude: position.coords.latitude, longitude: position.coords.longitude };

          const response = await makeApiCall(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });

          if (response.ok) {
            setSuccess(action === 'in' ? t('jobs.clockedInSuccessfully') : t('jobs.clockedOutSuccessfully'));
            fetchTodaysJobs();
          } else {
            const errorData = await response.json();
            setError(errorData.error || (action === 'in' ? t('jobs.failedToClockIn') : t('jobs.failedToClockOut')));
          }
          setClockingJobId(null);
        },
        () => {
          setError(t('common.error'));
          setClockingJobId(null);
        }
      );
    } catch (err) {
      setError(t('common.error'));
      setClockingJobId(null);
    }
  }, [t, fetchTodaysJobs]);

  const getCustomerDisplay = (job) => {
    if (job.company_name) return job.company_name;
    return [job.first_name, job.last_name].filter(Boolean).join(' ') || '—';
  };

  const getAddressDisplay = (job) => {
    return [job.street_address, job.city].filter(Boolean).join(', ') || '—';
  };

  const getJobTypeColor = (type) => {
    if (type === 'summer') return 'success';
    if (type === 'winter') return 'info';
    if (type === 'construction') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={32} />
      </Paper>
    );
  }

  if (jobs.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }} aria-label={t('jobs.todayJobs')}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Work sx={{ color: '#2e7d32' }} />
          {t('jobs.todayJobs')} ({jobs.length})
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchTodaysJobs} size="small" aria-label="Refresh today's jobs">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('jobs.jobNumber')}</TableCell>
              <TableCell>{t('jobs.customer')}</TableCell>
              <TableCell>{t('quotes.address')}</TableCell>
              <TableCell>{t('jobs.status')}</TableCell>
              <TableCell align="center">{t('jobs.clockIn')} / {t('jobs.clockOut')}</TableCell>
              {onJobClick && <TableCell align="center"></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => {
              const isClockedIn = !!job.clock_in_time && !job.clock_out_time;
              const isComplete = !!job.clock_out_time || job.is_complete;
              const isClocking = clockingJobId === job.id;

              return (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {job.job_number}
                    </Typography>
                  </TableCell>
                  <TableCell>{getCustomerDisplay(job)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{getAddressDisplay(job)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        isComplete ? t('jobs.completed') :
                        isClockedIn ? t('jobs.inProgress') :
                        t('jobs.scheduled')
                      }
                      color={
                        isComplete ? 'default' :
                        isClockedIn ? 'success' :
                        'primary'
                      }
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={job.job_type}
                      color={getJobTypeColor(job.job_type)}
                      size="small"
                      sx={{ ml: 0.5, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {isComplete ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('jobs.completed')}
                      </Typography>
                    ) : isClockedIn ? (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={isClocking ? <CircularProgress size={16} color="inherit" /> : <ClockOutIcon />}
                        onClick={() => handleClockAction(job, 'out')}
                        disabled={isClocking}
                        aria-label={`${t('jobs.clockOut')} - ${getCustomerDisplay(job)}`}
                        sx={{ fontWeight: 'bold', minWidth: 130 }}
                      >
                        {t('jobs.clockOut')}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={isClocking ? <CircularProgress size={16} color="inherit" /> : <ClockInIcon />}
                        onClick={() => handleClockAction(job, 'in')}
                        disabled={isClocking}
                        aria-label={`${t('jobs.clockIn')} - ${getCustomerDisplay(job)}`}
                        sx={{ fontWeight: 'bold', minWidth: 130 }}
                      >
                        {t('jobs.clockIn')}
                      </Button>
                    )}
                  </TableCell>
                  {onJobClick && (
                    <TableCell align="center">
                      <Tooltip title={t('jobs.jobDetails')}>
                        <IconButton
                          size="small"
                          onClick={() => onJobClick(job.id)}
                          aria-label={`${t('jobs.jobDetails')} - ${job.job_number}`}
                        >
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default TodaysJobsWidget;
